import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatBottomSheet } from '@angular/material';
import { ActivatedRoute, Router } from '@angular/router';
import { EnquiryComponent } from 'src/app/enquiry/enquiry.component';
import { DataService } from 'src/app/services/data.service';
import { EnquiryStateService } from 'src/app/services/enquiry-state.service';
import { LocationMappingService } from 'src/app/services/location-mapping.service';
import { checkCookieForUsername, slugify } from 'src/app/Utils/utils';
import { Title, Meta } from '@angular/platform-browser';

export interface Features {
  assignment: string;
  internship: string;
  certificate: string;
}

const ELEMENT_DATA: Features[] = [
  {assignment: 'Available', internship: 'Available', certificate: 'Available'},
];

@Component({
  selector: 'app-course-detail',
  templateUrl: './course-detail.component.html',
  styleUrls: ['./course-detail.component.css']
})
export class CourseDetailComponent implements OnInit, OnDestroy {
  public course;
  public dataSource = ELEMENT_DATA;
  displayedColumns: string[] = ['assignment', 'certificate', 'internship'];
  public branchList: any[] = [];
  public contactNumber: string = '918618958330';
  public whatsappNumber: string = '918618958330';
  private enquiryTimerId: any = null;
  private userInteracted = false;
  private interactionSelectors = ['#enrollNowBtn', '#callNowBtn', '#whatsappBtn', '#bookDemoBtn', '#quickEnquiryBtn'];
  private interactionListener = () => { this.userInteracted = true; };
  
  public courseFAQs = [
    {
      question: 'What is the duration of this course?',
      answer: 'The course duration varies by program, typically ranging from 3 to 6 months depending on the batch type (regular, weekend, or fast-track) you choose.'
    },
    {
      question: 'Do I receive a certificate upon completion?',
      answer: 'Yes, upon successful completion of the course, you will receive a verifiable NICT certificate that can be validated on our website.'
    },
    {
      question: 'Is job assistance provided?',
      answer: 'Absolutely! We provide comprehensive job assistance including resume building, interview preparation, and connections with our placement partners.'
    },
    {
      question: 'What are the prerequisites for this course?',
      answer: 'Basic computer knowledge is sufficient for most courses. Some advanced courses may have specific prerequisites which will be mentioned in the course details.'
    },
    {
      question: 'Are there flexible batch timings available?',
      answer: 'Yes, we offer multiple batch options including regular (Mon-Fri), weekend (Sat-Sun), and fast-track batches to accommodate your schedule.'
    },
    {
      question: 'Can I attend a demo class before enrolling?',
      answer: 'Yes! We encourage prospective students to attend a free demo session. Contact us to schedule your demo class.'
    },
    {
      question: 'What is the fee structure?',
      answer: 'Our fees are very competitive and affordable. The exact fee depends on the course. Please contact our center or fill the enquiry form for detailed fee information.'
    },
    {
      question: 'Do you provide study materials?',
      answer: 'Yes, comprehensive digital study materials and course notes are provided free of cost to all enrolled students.'
    }
  ];

  constructor(
    private route: ActivatedRoute,
    private dataService: DataService,
    private bottomSheet: MatBottomSheet,
    private enquiryStateService: EnquiryStateService,
    private locationMappingService: LocationMappingService,
    private titleService: Title,
    private metaService: Meta,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.dataService.getCachedBranches().subscribe(branches => {
      this.branchList = branches;
      this.updateContactNumbers();
    });
    
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const courseName = this.route.snapshot.paramMap.get('courseName');
    this.getCourseDetails(courseName);

    // Attach listeners to tracked buttons (by class or id)
    setTimeout(() => {
      this.interactionSelectors.forEach(sel => {
        const btn = document.querySelector(sel);
        if (btn) btn.addEventListener('click', this.interactionListener, { once: true });
      });
    }, 0);

    // Store the timer id so it can be cancelled in ngOnDestroy - otherwise it keeps
    // running after the user navigates away and pops the bottom sheet open on whatever
    // page they land on next (duplicate/leftover enquiry popups).
    this.enquiryTimerId = setTimeout(() => {
      if (!this.enquiryStateService.hasEnquiryBeenSubmitted() && 
          !this.enquiryStateService.hasEnquiryBeenShown()) {
        this.enquiryStateService.markEnquiryAsShown();
        const locationFromUrl = this.getLocationFromUrl();
        const source = locationFromUrl ? 'ads_landing_page_forced' : 'landing_page_forced';
        this.bottomSheet.open(EnquiryComponent,{
          data: {
            courseName: this.course.courseName,
            source: source,
            location: locationFromUrl
          },
          disableClose: !this.userInteracted
        });
      }
    }, 10000);
  }

  ngOnDestroy(): void {
    if (this.enquiryTimerId) {
      clearTimeout(this.enquiryTimerId);
      this.enquiryTimerId = null;
    }
    this.interactionSelectors.forEach(sel => {
      const btn = document.querySelector(sel);
      if (btn) btn.removeEventListener('click', this.interactionListener);
    });
  }

  getLocationFromUrl(): string | null {
    const locationParam = this.route.snapshot.paramMap.get('location');
    if (locationParam) {
      return this.locationMappingService.getLocationAndStore(locationParam);
    }
    return this.locationMappingService.getStoredLocation();
  }

  getCourseDetails(courseName: string) {
    this.dataService.getCachedCourses().subscribe(res => {
      this.course = res.find(course => slugify(course.courseName.trim()) === courseName.trim());

      if (this.course) {
        const title = this.course.courseName + ' Course | Best ' + this.course.courseName + ' Training in Bangalore | NICT Computer Education';
        const description = 'Learn ' + this.course.courseName + ' with NICT expert-led training in Bangalore. Get certification, assignments, and internship support. Enroll now!';
        const image = this.course.displayBanner || 'https://nictcomputereducation.com/assets/logo.png';
        const keywords = this.course.seoKeywords || '';

        this.titleService.setTitle(title);
        this.metaService.updateTag({ name: 'description', content: description });
        this.metaService.updateTag({ name: 'keywords', content: keywords });
        this.metaService.updateTag({ property: 'og:title', content: title });
        this.metaService.updateTag({ property: 'og:description', content: description });
        this.metaService.updateTag({ property: 'og:image', content: image });
      }
    });
  }

  enrollNow(courseName: String): void {
    const locationFromUrl = this.getLocationFromUrl();
    const eventName = locationFromUrl ? 'ads_enroll_now_click' : 'enroll_now_click';
    if (typeof window !== 'undefined') {
      if ((window as any).dataLayer) {
        (window as any).dataLayer.push({
          event: eventName,
          course_name: this.course ? this.course.courseName : '',
          page: 'course_detail',
          location: locationFromUrl
        });
      }
      if ((window as any).dataLayer2) {
        (window as any).dataLayer2.push({
          event: eventName,
          course_name: this.course ? this.course.courseName : '',
          page: 'course_detail',
          location: locationFromUrl
        });
      }
    }
    const source = locationFromUrl ? 'ads_landing_page_enroll_now' : 'landing_page_enroll_now';
    this.bottomSheet.open(EnquiryComponent, {
      data: {
        courseName: courseName,
        source: source,
        location: locationFromUrl
      }
    });
  }

  makeCall(): void {
    const locationFromUrl = this.getLocationFromUrl();
    const eventName = locationFromUrl ? 'ads_call_button_click' : 'call_button_click';
    if (typeof window !== 'undefined') {
      if ((window as any).dataLayer) {
        (window as any).dataLayer.push({
          event: eventName,
          course_name: this.course ? this.course.courseName : '',
          page: 'course_detail',
          location: locationFromUrl
        });
      }
      if ((window as any).dataLayer2) {
        (window as any).dataLayer2.push({
          event: eventName,
          course_name: this.course ? this.course.courseName : '',
          page: 'course_detail',
          location: locationFromUrl
        });
      }
    }
    window.location.href = 'tel:' + this.contactNumber;
  }

  openWhatsApp(): void {
    const locationFromUrl = this.getLocationFromUrl();
    const eventName = locationFromUrl ? 'ads_whatsapp_button_click' : 'whatsapp_button_click';
    if (typeof window !== 'undefined') {
      if ((window as any).dataLayer) {
        (window as any).dataLayer.push({
          event: eventName,
          course_name: this.course ? this.course.courseName : '',
          page: 'course_detail',
          location: locationFromUrl
        });
      }
      if ((window as any).dataLayer2) {
        (window as any).dataLayer2.push({
          event: eventName,
          course_name: this.course ? this.course.courseName : '',
          page: 'course_detail',
          location: locationFromUrl
        });
      }
    }
    const courseName = this.course ? this.course.courseName : 'the';
    const msg = 'Hi, I am interested in ' + courseName + ' course';
    const message = encodeURIComponent(msg);
    const url = 'https://wa.me/' + this.whatsappNumber + '?text=' + message;
    window.open(url, '_blank');
  }

  bookDemoClass(): void {
    const locationFromUrl = this.getLocationFromUrl();
    const eventName = locationFromUrl ? 'ads_book_demo_click' : 'book_demo_click';
    if (typeof window !== 'undefined') {
      if ((window as any).dataLayer) {
        (window as any).dataLayer.push({
          event: eventName,
          course_name: this.course ? this.course.courseName : '',
          page: 'course_detail',
          location: locationFromUrl
        });
      }
      if ((window as any).dataLayer2) {
        (window as any).dataLayer2.push({
          event: eventName,
          course_name: this.course ? this.course.courseName : '',
          page: 'course_detail',
          location: locationFromUrl
        });
      }
    }
    const source = locationFromUrl ? 'ads_landing_page_book_demo' : 'landing_page_book_demo';
    this.bottomSheet.open(EnquiryComponent, {
      data: {
        courseName: this.course ? this.course.courseName : '',
        source: source,
        location: locationFromUrl
      }
    });
  }

  quickEnquiry(): void {
    const locationFromUrl = this.getLocationFromUrl();
    const eventName = locationFromUrl ? 'ads_quick_enquiry_click' : 'quick_enquiry_click';
    const source = locationFromUrl ? 'ads_landing_page_quick_enquiry' : 'landing_page_quick_enquiry';
    if (typeof window !== 'undefined') {
      if ((window as any).dataLayer) {
        (window as any).dataLayer.push({
          event: eventName,
          course_name: this.course ? this.course.courseName : '',
          page: 'course_detail',
          location: locationFromUrl
        });
      }
      if ((window as any).dataLayer2) {
        (window as any).dataLayer2.push({
          event: eventName,
          course_name: this.course ? this.course.courseName : '',
          page: 'course_detail',
          location: locationFromUrl
        });
      }
    }
    this.bottomSheet.open(EnquiryComponent, {
      data: {
        courseName: this.course ? this.course.courseName : '',
        source: source,
        location: locationFromUrl
      }
    });
  }

  private updateContactNumbers(): void {
    const location = this.getLocationFromUrl();
    if (location && this.branchList.length > 0) {
      const branch = this.branchList.find(b => 
        b.location.toLowerCase() === location.toLowerCase()
      );
      if (branch && branch.mobile) {
        const mobileNumber = branch.mobile.replace(/\D/g, '');
        this.contactNumber = mobileNumber;
        this.whatsappNumber = mobileNumber;
      }
    }
  }
}
